import { FiChevronRight } from 'react-icons/fi';
import { Link, useLocation } from 'react-router';
import { isCurrentMenuItem, type MegaMenuData, type MenuItem } from '@/data/navigation';
import { PRODUCT_URLS } from '@podus/utils';
import { Img } from '@/components/Img';

/** `current` is the page you are on — never a fixed position in the list. */
function Item({ item, current, onNavigate }: { item: MenuItem; current: boolean; onNavigate?: () => void }) {
    return (
        <Link
            to={item.href}
            onClick={onNavigate}
            aria-current={current ? 'page' : undefined}
            className={`group flex w-full max-w-[244px] flex-col gap-3 rounded-xl p-4 transition-colors duration-200 ${
                current ? 'bg-[#f5f5f5] hover:bg-[#ececec]' : 'hover:bg-[#f5f5f5]'
            }`}
        >
            <div className="flex items-center gap-3">
                <Img src={item.icon} alt="" className="h-6 w-6 shrink-0 object-contain" />
                <span className="flex-1 text-[16px] font-bold leading-[20px] tracking-[-0.32px] text-[#171717]">
                    {item.label}
                </span>
                <FiChevronRight className="h-4 w-4 shrink-0 text-[#a9a9a9] transition-colors duration-200 group-hover:text-[#171717]" />
            </div>
            <p
                className={`text-[12px] leading-[18px] tracking-[-0.12px] transition-colors duration-200 ${
                    current ? 'text-[#434343]' : 'text-[#918ea4] group-hover:text-[#434343]'
                }`}
            >
                {item.description}
            </p>
        </Link>
    );
}

/** Mock "Podus AI / My Contract" preview card shown inside the Innovations mega-menu. */
function InnovationsPreview() {
    const bar = (w: string) => <div className="h-[6px] rounded-full bg-[#f0f0f0]" style={{ width: w }} />;
    const ContractCard = ({ shadow }: { shadow?: boolean }) => (
        <div
            className={`flex w-[150px] flex-col gap-3 rounded-xl bg-white p-3 ${
                shadow ? 'shadow-[-24px_24px_72px_0px_rgba(0,0,0,0.12)]' : 'border border-[#f0f0f0]'
            }`}
        >
            <span className="text-[6px] font-bold text-[#171717]">My Contract</span>
            {['Summary', 'Experience', 'Skills', 'Education'].map((label) => (
                <div key={label} className="flex flex-col gap-1.5">
                    <span className="text-[6px] font-bold text-[#434343]">{label}</span>
                    {bar('100%')}
                    {bar('70%')}
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex w-[420px] shrink-0 flex-col gap-5 rounded-3xl bg-[#e9e9e9] p-5">
            <div className="flex justify-center gap-3 overflow-hidden rounded-2xl bg-white p-5">
                <ContractCard />
                <ContractCard shadow />
            </div>
            <div className="flex flex-col gap-2">
                <h4 className="text-[16px] font-bold text-[#171717]">Podus AI</h4>
                <p className="max-w-[315px] text-[12px] leading-[18px] text-[#918ea4]">
                    Podus is an AI-powered super app transforming how people and businesses access legal solutions —
                    from instant guidance to contract management and human rights protection.
                </p>
                <a
                    href={PRODUCT_URLS.podusai}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex w-fit items-center justify-center rounded-full bg-[#151515] px-8 py-3 text-[14px] font-bold text-white"
                >
                    Get Started
                </a>
            </div>
        </div>
    );
}

export default function MegaMenu({ data, onNavigate }: { data: MegaMenuData; onNavigate?: () => void }) {
    const isInnovations = data.key === 'innovations';
    const { pathname, hash } = useLocation();

    return (
        <div className="rounded-b-2xl bg-white p-8 shadow-[0_31px_53px_1px_rgba(0,0,0,0.05)] lg:p-10">
            <div className="mb-6 flex items-center justify-between">
                <h3 className="text-[28px] font-bold leading-[1.25] tracking-[-0.5px] text-[#a9a9a9] lg:text-[32px]">
                    {data.title}
                </h3>
                {isInnovations && (
                    <Link
                        to="/projects"
                        onClick={onNavigate}
                        className="hidden items-center justify-center rounded-[10px] bg-[#1b1b1b] px-6 py-3.5 text-[16px] font-bold text-white lg:inline-flex"
                    >
                        View All Innovations
                    </Link>
                )}
            </div>

            {isInnovations ? (
                <div className="flex gap-6">
                    <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-3 xl:grid-cols-4">
                        {data.items.map((item) => (
                            <Item
                                key={item.label}
                                item={item}
                                current={isCurrentMenuItem(item.href, pathname, hash)}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </div>
                    <div className="hidden xl:block">
                        <InnovationsPreview />
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-3">
                    {data.items.map((item) => (
                        <Item
                            key={item.label}
                            item={item}
                            current={isCurrentMenuItem(item.href, pathname, hash)}
                            onNavigate={onNavigate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
